import { useState, type FormEvent } from 'react';
import { Navigate } from 'react-router';
import { api, ApiError, errorMessage } from '../../shared/api/client';
import type { MultimediaDevice } from '../../shared/api/types';
import { Alert, Button, Field, PasswordField } from '../../shared/ui/components';
import { AuthLayout } from '../auth/AuthLayout';
import { useSession } from '../session/SessionProvider';

/**
 * Entrada de la app KEORA Multimedia: nunca abre el panel de nadie. Quien opera Presentaciones
 * escribe su correo y contraseña, elige la iglesia y la computadora queda vinculada como equipo de
 * multimedia en el mismo paso; su sesión personal se cierra sola. Si no se puede vincular, la sesión
 * personal se cierra también, para que no quede abierta en la computadora de proyección.
 */
export function DeviceLinkPage() {
  const { state, login, logout, refresh } = useSession();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState('Computadora de proyección');
  const [churchId, setChurchId] = useState('');
  const [conflict, setConflict] = useState<MultimediaDevice[] | null>(null);

  if (state.status === 'loading') return null;
  if (state.status === 'authenticated' && state.me.isDevice) return <Navigate to="/app/presentaciones" replace />;
  const me = state.status === 'authenticated' ? state.me : null;
  const churches = me?.contexts ?? [];
  const chosen = churchId || (churches.length === 1 ? churches[0]!.churchId : '');

  async function cancel() {
    await logout().catch(() => undefined);
    setConflict(null);
    setError(null);
  }

  async function link(replace: boolean) {
    if (!chosen) return setError('Elige la iglesia.');
    setBusy(true);
    setError(null);
    try {
      await api.put('/security/context', { churchId: chosen });
      await api.post(`/security/churches/${chosen}/devices`, { name, replace });
      await refresh();
    } catch (e) {
      const details = e instanceof ApiError ? (e.details as { reason?: string; devices?: MultimediaDevice[] } | undefined) : undefined;
      if (details?.reason === 'device_limit') setConflict(details.devices ?? []);
      else {
        // Sin permiso (u otro error): no se deja la sesión personal abierta en esta computadora.
        setError(e instanceof ApiError && e.status === 403 ? 'Solo quien opera Proyectar en la iglesia puede vincular el equipo.' : errorMessage(e));
        await logout().catch(() => undefined);
      }
    } finally {
      setBusy(false);
    }
  }

  async function onLogin(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setBusy(true);
    setError(null);
    try {
      // Sin «mantener sesión»: si algo falla, la sesión personal no sobrevive al cerrar la app.
      await login(String(form.get('email')).trim(), String(form.get('password')), false);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout title="Equipo de multimedia">
      <p className="muted">Vincula esta computadora a tu iglesia. Desde entonces entra sola con Proyectar, Cantos, Multimedia y Biblia; tu cuenta no queda abierta aquí.</p>
      {error ? <Alert>{error}</Alert> : null}
      {!me ? (
        <form className="form" onSubmit={onLogin} noValidate>
          <Field label="Correo" name="email" type="email" autoComplete="off" required />
          <PasswordField label="Contraseña" name="password" autoComplete="off" required />
          <Button type="submit" busy={busy}>
            Continuar
          </Button>
        </form>
      ) : conflict ? (
        <div className="form">
          <Alert tone="info">
            Ya está vinculado {conflict.map((d) => `«${d.name}»`).join(', ')}. Si vinculas esta computadora, {conflict.length === 1 ? 'esa se desconecta' : 'la más antigua se desconecta'} al instante.
          </Alert>
          <Button busy={busy} onClick={() => void link(true)}>
            Reemplazar y vincular esta
          </Button>
          <Button type="button" variant="ghost" onClick={() => void cancel()}>
            Cancelar
          </Button>
        </div>
      ) : (
        <form
          className="form"
          onSubmit={(e) => {
            e.preventDefault();
            void link(false);
          }}
        >
          {churches.length > 1 ? (
            <div className="field">
              <label htmlFor="device-church">Iglesia</label>
              <select id="device-church" value={chosen} required onChange={(e) => setChurchId(e.target.value)}>
                <option value="">Elige…</option>
                {churches.map((c) => (
                  <option key={c.churchId} value={c.churchId}>
                    {c.churchName}
                  </option>
                ))}
              </select>
            </div>
          ) : churches.length === 1 ? (
            <p>
              Iglesia: <strong>{churches[0]!.churchName}</strong>
            </p>
          ) : (
            <Alert>Tu cuenta no pertenece a ninguna iglesia.</Alert>
          )}
          <Field label="Nombre de esta computadora" value={name} maxLength={60} required onChange={(e) => setName(e.target.value)} />
          <Button type="submit" busy={busy} disabled={churches.length === 0}>
            Vincular esta computadora
          </Button>
          <Button type="button" variant="ghost" onClick={() => void cancel()}>
            Cancelar y cerrar sesión
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}
